// =====================================================================
// AutomationsDialog (Quadros PRO / Onda 2 / S60) — regras estilo Butler.
// Lista + cria + ativa/desativa + remove. WhatsApp fica de fora (anti-ban).
// =====================================================================
import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem,
  Box, Typography, IconButton, Switch, CircularProgress
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import { toast } from "react-toastify";
import {
  listAutomations, createAutomation, updateAutomationRule, removeAutomation, boardErrorMessage, PRIORITIES
} from "../../services/boardService";

const TRIGGERS = [
  { value: "card_moved_to_list", label: "Quando o card entra numa coluna" },
  { value: "card_created", label: "Quando um card é criado" }
];
const ACTIONS = [
  { value: "set_done", label: "Marcar como concluído" },
  { value: "set_priority", label: "Definir prioridade" },
  { value: "notify_members", label: "Notificar responsável/membros" },
  { value: "add_comment", label: "Adicionar comentário" },
  { value: "patient_history", label: "Registrar no histórico do paciente" }
];

const AutomationsDialog = ({ open, board, onClose }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const [triggerType, setTriggerType] = useState("card_moved_to_list");
  const [triggerListId, setTriggerListId] = useState("");
  const [actionType, setActionType] = useState("set_done");
  const [actionPriority, setActionPriority] = useState("alta");
  const [actionText, setActionText] = useState("");

  const lists = (board && board.lists) || [];

  const load = useCallback(async () => {
    if (!board) return;
    setLoading(true);
    try { const d = await listAutomations(board.id); setItems((d && d.automations) || []); }
    catch { setItems([]); } finally { setLoading(false); }
  }, [board]);

  useEffect(() => {
    if (open) {
      load();
      setName(""); setTriggerType("card_moved_to_list");
      setTriggerListId(lists[0] ? String(lists[0].id) : "");
      setActionType("set_done"); setActionPriority("alta"); setActionText("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleCreate = async () => {
    setSaving(true);
    try {
      const triggerConfig = triggerType === "card_moved_to_list" ? { listId: Number(triggerListId) } : null;
      let actionConfig = null;
      if (actionType === "set_priority") actionConfig = { priority: actionPriority };
      if (actionType === "add_comment") actionConfig = { text: actionText };
      if (actionType === "patient_history") actionConfig = { text: actionText || "Passou pela automação" };
      await createAutomation(board.id, { name, triggerType, triggerConfig, actionType, actionConfig, active: true });
      toast.success("Automação criada.");
      setName(""); load();
    } catch (e) { toast.error(boardErrorMessage(e)); } finally { setSaving(false); }
  };
  const toggle = async (a) => { try { await updateAutomationRule(a.id, { active: !a.active }); load(); } catch (e) { toast.error(boardErrorMessage(e)); } };
  const del = async (a) => { if (!window.confirm("Remover esta automação?")) return; try { await removeAutomation(a.id); load(); } catch (e) { toast.error(boardErrorMessage(e)); } };

  const listName = (id) => { const l = lists.find((x) => Number(x.id) === Number(id)); return l ? l.name : `#${id}`; };
  const trgLabel = (t) => (TRIGGERS.find((x) => x.value === t) || {}).label || t;
  const actLabel = (t) => (ACTIONS.find((x) => x.value === t) || {}).label || t;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Automações do quadro</DialogTitle>
      <DialogContent dividers>
        <Typography variant="caption" color="textSecondary">
          ⚠️ Envio automático de WhatsApp fica desativado por segurança (anti-ban) — habilitado só via Cloud API.
        </Typography>
        {loading ? (
          <Box p={2} display="flex" justifyContent="center"><CircularProgress size={20} /></Box>
        ) : (
          <Box mt={1}>
            {items.length === 0 ? (
              <Typography variant="body2" color="textSecondary">Nenhuma automação ainda.</Typography>
            ) : items.map((a) => (
              <Box key={a.id} display="flex" alignItems="center" style={{ gap: 6, marginBottom: 4 }}>
                <Switch size="small" checked={!!a.active} onChange={() => toggle(a)} color="primary" />
                <Typography variant="body2" style={{ flex: 1 }}>
                  <b>{a.name || "Regra"}:</b> {trgLabel(a.triggerType)}
                  {a.triggerType === "card_moved_to_list" && a.triggerConfig ? ` (${listName(a.triggerConfig.listId)})` : ""} → {actLabel(a.actionType)}
                </Typography>
                <IconButton size="small" onClick={() => del(a)}><DeleteIcon fontSize="small" /></IconButton>
              </Box>
            ))}
          </Box>
        )}
        <Box mt={2} p={1} style={{ border: "1px dashed #ccc", borderRadius: 8 }}>
          <Typography variant="subtitle2">Nova automação</Typography>
          <TextField size="small" label="Nome" value={name} onChange={(e) => setName(e.target.value)} fullWidth margin="dense" />
          <TextField select size="small" label="Quando" value={triggerType} onChange={(e) => setTriggerType(e.target.value)} fullWidth margin="dense">
            {TRIGGERS.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
          </TextField>
          {triggerType === "card_moved_to_list" && (
            <TextField select size="small" label="Coluna" value={triggerListId} onChange={(e) => setTriggerListId(e.target.value)} fullWidth margin="dense">
              {lists.map((l) => <MenuItem key={l.id} value={String(l.id)}>{l.name}</MenuItem>)}
            </TextField>
          )}
          <TextField select size="small" label="Então" value={actionType} onChange={(e) => setActionType(e.target.value)} fullWidth margin="dense">
            {ACTIONS.map((a) => <MenuItem key={a.value} value={a.value}>{a.label}</MenuItem>)}
          </TextField>
          {actionType === "set_priority" && (
            <TextField select size="small" label="Prioridade" value={actionPriority} onChange={(e) => setActionPriority(e.target.value)} fullWidth margin="dense">
              {PRIORITIES.filter((p) => p.value).map((p) => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
            </TextField>
          )}
          {(actionType === "add_comment" || actionType === "patient_history") && (
            <TextField size="small" label="Texto" value={actionText} onChange={(e) => setActionText(e.target.value)} fullWidth margin="dense" />
          )}
          <Box mt={1} display="flex" justifyContent="flex-end">
            <Button variant="contained" color="primary" size="small" onClick={handleCreate} disabled={saving}>Criar automação</Button>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Fechar</Button></DialogActions>
    </Dialog>
  );
};

export default AutomationsDialog;
